<script lang="ts" setup>
import { CommercialAgreement } from '@/core/collections/contracts-api/commercial-agreements/commercial-agreement.interface';
import {
   ContractTemplateFieldName,
   ContractTemplateFieldNameKey,
} from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import { useStore } from '@/core/hooks/useStore';
import { NxtFormSection } from '@nxtpeople/nxt-styles';
import { deepEqual } from '@nxtpeople/nxt-vue-library';

import { GROUPED_ACTIVITY_RISKS } from '@/core/collections/contracts-api/activity-risks/activity-risk.interface';
import { ContractTemplateField } from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import CommercialAgreementDropdown from '@/core/components/form-elements/CommercialAgreementDropdown.vue';
import dayjs from 'dayjs';
import { computed, onMounted, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContractTemplateWizardField from './ContractTemplateWizardField.vue';
import { useContractTemplate } from './useContractTemplate';
import { useContractTemplateKeys } from './useContractTemplateKeys';

const { t } = useI18n();
const { state, dispatch } = useStore();
const contractTemplate = useContractTemplate();
const { PERFORMANCE_DATA_FIELD_KEYS, INVOICE_COMMENT_FIELD_KEYS, CO_FIELD_KEYS } = useContractTemplateKeys();

//#region Props
const emits = defineEmits<{ (e: 'onUpdateField', payload: { fieldNameId: number; key: string; value: any }): void }>();
const props = defineProps({
   fields: { type: Array as PropType<Array<ContractTemplateField>>, default: () => [] },
   fieldNames: {
      type: Array as PropType<Array<ContractTemplateFieldName>>,
      default: () => [],
   },
   loading: { type: Boolean, default: false },
   errors: { type: Object, default: () => ({}) },
});

const { fields, fieldNames } = toRefs(props);
//#endregion

//#region Default commercial agreement
const commercialAgreementOptions = ref<Array<any>>([]);
const coFields = computed(() =>
   CO_FIELD_KEYS.map((fieldKey) => fields.value.find((f) => f?.field_key === fieldKey)).filter((f) => !!f?.value),
);

const coFieldValues = computed(() =>
   coFields.value.reduce((acc, cur) => {
      if (!cur?.field_key) return acc;
      return { ...acc, [cur.field_key]: cur?.value };
   }, {}),
);

const defaultCommercialAgreementOptionsQuery = computed(() => {
   const customer_id = contractTemplate.value.customer_id;
   const employer_id = contractTemplate.value.employer_id;
   const joint_committee_number = contractTemplate.value.user_company_joint_committee?.joint_committee?.number;
   const contract_from = dayjs().format('YYYY-MM-DD');

   if (!customer_id || !employer_id || !joint_committee_number) return;

   if (contractTemplate.value.id && Object.keys(coFieldValues.value)?.length <= 0) return;
   return { customer_id, employer_id, joint_committee_number, contract_from, ...coFieldValues.value };
});

watch(defaultCommercialAgreementOptionsQuery, getDefaultCommercialAgreementOptions, { immediate: true });
async function getDefaultCommercialAgreementOptions(query?: { [key: string]: any }, oldQuery?: { [key: string]: any }) {
   if (!oldQuery && contractTemplate.value.id) {
      return (commercialAgreementOptions.value = [contractTemplate.value.default_commercial_agreement]);
   }

   if (!query || (oldQuery && deepEqual(query, oldQuery))) return;

   try {
      commercialAgreementOptions.value = await dispatch('commercialAgreement/getCommercialAgreementsByIds', query);

      if (commercialAgreementOptions.value.find((co) => co.id === contractTemplate.value.default_commercial_agreement_id)) {
         setDefaultCommercialAgreement();
      }
      if (commercialAgreementOptions.value.length === 1) {
         setDefaultCommercialAgreement(commercialAgreementOptions.value[0]);
      }
   } catch {
      console.warn(`Could not fetch commercial agreements`);
   }
}

function setDefaultCommercialAgreement(commercialAgreement?: CommercialAgreement) {
   contractTemplate.value.default_commercial_agreement_id = commercialAgreement?.id;
   contractTemplate.value.default_commercial_agreement = commercialAgreement;
}

watch(commercialAgreementOptions, resetCommercialAgreement);

function resetCommercialAgreement(commercialAgreementOptions?: Array<CommercialAgreement>) {
   if (!commercialAgreementOptions || !commercialAgreementOptions.length) {
      setDefaultCommercialAgreement();
   }
}
//#endregion

//#region Activity Risk
const workerStatuteField = computed(() => fields.value.find((f) => f.field_key === ContractTemplateFieldNameKey.WorkerStatuteId));
const statute = computed(() => workerStatuteField.value?.selected_value);
const activityRisks = computed(() => state.activityRisk?.activityRisks || []);

const filteredActivityRiskOptions = computed(
   () => GROUPED_ACTIVITY_RISKS.find((x) => x.statutes.includes(statute.value?.key as any))?.activityRisks || [],
);
const activityRiskOptions = computed(() =>
   filteredActivityRiskOptions.value.map((aro) => activityRisks.value.find((ar) => ar.key === aro)).filter((ar) => !!ar),
);

onMounted(() => dispatch('activityRisk/getActivityRisks'));
//#endregion

//#region Fields
const performanceDataFieldsNames = computed(() =>
   fieldNames.value
      .filter((fn) => PERFORMANCE_DATA_FIELD_KEYS.value.find(({ key }) => fn.key === key))
      .map((pdfn) => {
         if (pdfn.key === ContractTemplateFieldNameKey.ActivityRiskId) {
            return { ...pdfn, options: activityRiskOptions.value };
         }
         return { ...pdfn };
      }),
);
const invoiceCommentFields = computed(() =>
   fieldNames.value.filter((fn) => INVOICE_COMMENT_FIELD_KEYS.value.find(({ key }) => fn.key === key)),
);
function getField(fieldNameId: number) {
   return fields.value.find((field) => field.contract_template_field_name_id === fieldNameId);
}
//#endregion
</script>

<template>
   <div class="contract-template-wizard-performance-data">
      <NxtFormSection :title="t('contracts.performance-data', 2)">
         <div class="contract-template-wizard-performance-data__section">
            <ContractTemplateWizardField
               :key="performanceDataFieldsNames[0].key"
               :errors="errors"
               :mode="'light'"
               :disabled="loading"
               :field-name="performanceDataFieldsNames[0]"
               :field="getField(performanceDataFieldsNames[0].id)"
               :hide-calculated-values="!performanceDataFieldsNames[0].allows_conditions"
               @on-update-field="emits('onUpdateField', $event)"
            />

            <CommercialAgreementDropdown
               :model-value="contractTemplate.default_commercial_agreement_id"
               :name="'default_commercial_agreement_id'"
               :label="$t('contracts.choose-commercial-agreement')"
               :options="commercialAgreementOptions"
               :required="true"
               :mode="'light'"
               :disabled="loading"
               :errors="errors"
               :display-key="`performance_code.name.${$i18n.locale}`"
               :emit-object="true"
               :has-clear-option="true"
               @update:model-value="setDefaultCommercialAgreement"
            />

            <ContractTemplateWizardField
               v-for="fieldName in [...performanceDataFieldsNames].slice(1, performanceDataFieldsNames.length)"
               :key="fieldName.key"
               :mode="'light'"
               :errors="errors"
               :disabled="loading"
               :field-name="fieldName"
               :field="getField(fieldName.id)"
               :hide-calculated-values="!fieldName.allows_conditions"
               @on-update-field="emits('onUpdateField', $event)"
            />
         </div>
      </NxtFormSection>

      <NxtFormSection :title="t('contracts.invoice-comment')">
         <div class="contract-template-wizard-performance-data__section">
            <ContractTemplateWizardField
               v-for="fieldName in invoiceCommentFields"
               :key="fieldName.key"
               :mode="'light'"
               :errors="errors"
               :disabled="loading"
               :field-name="fieldName"
               :field="getField(fieldName.id)"
               :hide-calculated-values="!fieldName.allows_conditions"
               @on-update-field="emits('onUpdateField', $event)"
            />
         </div>
      </NxtFormSection>
   </div>
</template>

<style lang="scss" scoped>
.contract-template-wizard-performance-data {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
}
.contract-template-wizard-performance-data__section {
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: var(--nxt-gutter);
   margin-right: toRem(250);
}
</style>
